import { useState } from 'react';
import { Controller } from 'react-hook-form';
import {
  ToggleInput,
  InputText,
  IconInfo,
  Select,
  Checkbox,
  InputNumber,
  AccountsSelector,
  Autocomplete,
} from '@ui/src';
import { Category } from '@ui/src/components/accounts-selector/types';
import SelectedPrimaryAccount from './selected-primary-accounts';
import Schedule from './schedule';
import { AutoSettingsFormTypes } from './types';

import cn from './auto-settings.module.css';

const AutoSettingsForm = ({
  control,
  errors,
  currencyFrom,
  currencies,
  activeSettingsVisible,
  currenciesVisibile,
  currencyTo,
  schedule,
  setSchedule,
  general,
  isEditable,
  weekdaysWithErrors,
  setValue,
  exchangeType,
  onStatusChange,
  changeStatusInProgress,
  accountCategories,
  accountsCategoriesLoading,
  accountsType,
  selectedPrimaryAccounts,
  setSelectedPrimaryAccounts,
  primaryAccountsList,
  createSyncedTransaction,
  fromAccounts,
  fromAccountsListIsLoading,
  fromAccountsNextPageIsLoading,
  onScrollFromAccounts,
  fromAccountsPagination,
  toAccounts,
  toAccountsListIsLoading,
  toAccountsNextPageIsLoading,
  onScrollToAccounts,
  toAccountsPagination,
  onSearchAutocomplete,
  rateSource,
}: AutoSettingsFormTypes) => {
  const [
    selecteDPrimaryAccountsCollapsed,
    setSelectedPrimaryAccountsCollapsed,
  ] = useState(true);

  return (
    <div className={cn.autoSettingsFormContent}>
      {activeSettingsVisible && (
        <div className={cn.autoSettingsActiveWrapper}>
          <Controller
            control={control}
            defaultValue={false}
            name="active"
            render={({ field: { value, name, onChange } }) => (
              <ToggleInput
                name={name}
                checked={value}
                title="Active settings"
                onChange={(e) => {
                  if (onStatusChange) {
                    onStatusChange(e.target.checked);
                  } else {
                    onChange(e.target.checked);
                  }
                }}
                disabled={!isEditable || changeStatusInProgress}
              />
            )}
          />
          <div className={cn.hintWrapper}>
            <IconInfo width={16} height={16} />
          </div>
        </div>
      )}
      {currenciesVisibile && (
        <div
          className={cn.currenciesWrapper}
          style={{ zIndex: 13, position: 'relative' }}
        >
          <div className={cn.currency}>
            <Controller
              control={control}
              defaultValue=""
              name="exchangeCurrencyFromId"
              render={({ field: { onChange, value, name } }) => (
                <Select
                  name={name}
                  onChange={onChange}
                  value={value}
                  list={
                    currencies?.map((item) => ({
                      name: item.name,
                      value: item.id,
                    })) || []
                  }
                  placeholder="Select currency"
                  title="Currency from"
                  error={errors?.exchangeCurrencyFromId?.message}
                  disabled={!isEditable}
                  fullWidth
                />
              )}
            />
          </div>
          <div className={cn.currency}>
            <Controller
              control={control}
              defaultValue=""
              name="exchangeCurrencyToId"
              render={({ field: { onChange, value, name } }) => (
                <Select
                  name={name}
                  onChange={onChange}
                  value={value}
                  list={
                    currencies
                      ?.filter((item) => item.id !== currencyFrom)
                      ?.map((item) => ({
                        name: item.name,
                        value: item.id,
                      })) || []
                  }
                  placeholder="Select currency"
                  title="Currency to"
                  error={errors?.exchangeCurrencyToId?.message}
                  disabled={!currencyFrom || !isEditable}
                  fullWidth
                />
              )}
            />
          </div>
        </div>
      )}

      <div className={cn.formGroup}>
        <Controller
          control={control}
          name="rateSourceLifetimeSeconds"
          defaultValue=""
          render={({ field: { onChange, value, name } }) => (
            <InputText
              placeholder="Enter lifetime in seconds"
              name={name}
              value={value}
              title="Exchange rate lifetime for auto execution"
              onChange={(e) => {
                if (Number(e.target.value) < 1) {
                  onChange(1);
                } else {
                  onChange(e.target.value);
                }
              }}
              error={errors?.rateSourceLifetimeSeconds?.message}
              className={cn.inputWithSuffix}
              suffix="Sec"
              disabled={!isEditable}
              min={1}
            />
          )}
        />
      </div>
      <div
        className={cn.formGroup}
        style={{ zIndex: 12, position: 'relative' }}
      >
        <Controller
          control={control}
          name="targetStatus"
          defaultValue=""
          render={({ field: { onChange, value, name } }) => (
            <Select
              placeholder="Select target status"
              name={name}
              value={value}
              list={[
                {
                  name: 'ARCH',
                  value: 'ARCH',
                },
                {
                  name: 'Booked',
                  value: 'BOOKED',
                },
              ]}
              title="Target Status"
              onChange={onChange}
              error={errors?.targetStatus?.message}
              disabled={!isEditable}
            />
          )}
        />
      </div>
      <div className={cn.separator} />
      <div
        className={cn.formGroup}
        style={{ zIndex: 11, position: 'relative' }}
      >
        <Controller
          control={control}
          name="accountsType"
          defaultValue="ALL"
          render={({ field: { onChange, value, name } }) => (
            <Select
              name={name}
              value={value}
              title="Accounts scope"
              onChange={(e) => {
                onChange(e);

                if (
                  accountsType === 'SPECIFIC' &&
                  e.target.value === 'ALL' &&
                  setValue
                ) {
                  setValue('primaryAccountIds', []);
                  setSelectedPrimaryAccounts([]);
                  setSelectedPrimaryAccountsCollapsed(false);
                }
              }}
              placeholder="Select accounts scope"
              list={[
                {
                  name: 'All',
                  value: 'ALL',
                },
                {
                  name: 'Specific',
                  value: 'SPECIFIC',
                },
              ]}
            />
          )}
        />
      </div>
      {accountsType === 'SPECIFIC' && (
        <div className={cn.formGroup}>
          <Controller
            control={control}
            name="primaryAccountIds"
            defaultValue={[]}
            render={({ field: { onChange, value } }) => (
              <>
                <AccountsSelector
                  accounts={accountCategories as Category[]}
                  placeholder="Select primary accounts"
                  isLoading={accountsCategoriesLoading}
                  setAccountQuery={() => {}}
                  setSelectedAccount={() => {}}
                  setAccounts={() => {}}
                  title="Primary Accounts"
                  setSelectedPrimaryAccounts={(list) => {
                    onChange(list);
                    setSelectedPrimaryAccounts(list);
                  }}
                  selectedPrimaryAccountsList={value}
                  error={errors?.primaryAccountIds?.message}
                  primaryAccountsSelector
                />
                {selectedPrimaryAccounts &&
                  selectedPrimaryAccounts?.length > 0 && (
                    <div className={cn.selectedPrimaryAccountsList}>
                      <div className={cn.selectedPrimaryAccountsListContent}>
                        {selectedPrimaryAccounts?.slice(0, 5)?.map((item) => (
                          <SelectedPrimaryAccount
                            key={item}
                            selectedPrimaryAccount={item}
                            setSelectedPrimaryAccountsCollapsed={
                              setSelectedPrimaryAccountsCollapsed
                            }
                            setSelectedPrimaryAccounts={
                              setSelectedPrimaryAccounts
                            }
                            primaryAccountsList={primaryAccountsList}
                            accountCategories={accountCategories}
                            selectedPrimaryAccounts={selectedPrimaryAccounts}
                            onChange={onChange}
                          />
                        ))}
                        {!selecteDPrimaryAccountsCollapsed &&
                          selectedPrimaryAccounts
                            ?.slice(5)
                            ?.map((item) => (
                              <SelectedPrimaryAccount
                                key={item}
                                selectedPrimaryAccount={item}
                                setSelectedPrimaryAccountsCollapsed={
                                  setSelectedPrimaryAccountsCollapsed
                                }
                                setSelectedPrimaryAccounts={
                                  setSelectedPrimaryAccounts
                                }
                                primaryAccountsList={primaryAccountsList}
                                accountCategories={accountCategories}
                                selectedPrimaryAccounts={
                                  selectedPrimaryAccounts
                                }
                                onChange={onChange}
                              />
                            ))}
                        {selectedPrimaryAccounts?.length > 5 && (
                          <button
                            type="button"
                            className={cn.showAllAccounts}
                            onClick={() =>
                              setSelectedPrimaryAccountsCollapsed(
                                !selecteDPrimaryAccountsCollapsed,
                              )
                            }
                          >
                            {selecteDPrimaryAccountsCollapsed
                              ? `+ ${selectedPrimaryAccounts?.slice(5)?.length}`
                              : 'Show less'}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
              </>
            )}
          />
        </div>
      )}

      <div className={cn.separator} />
      <div className={cn.formGroup}>
        <Controller
          control={control}
          name="createChildrenTransactions"
          defaultValue={false}
          render={({ field: { onChange, value } }) => (
            <ToggleInput
              name="createSyncedTransaction"
              checked={value}
              onChange={(e) => {
                onChange(e.target.checked);

                if (!e.target.checked && setValue) {
                  setValue('fromAccountId', '');
                  setValue('toAccountId', '');
                  setValue('rateSourceId', '');
                }
              }}
              title="Create cover trade transaction"
            />
          )}
        />
      </div>
      {createSyncedTransaction && (
        <>
          <div className={cn.formGroup}>
            <Controller
              control={control}
              name="fromAccountId"
              defaultValue=""
              render={({ field: { onChange, value, name } }) => (
                <Autocomplete
                  name={name}
                  value={value as string}
                  onChange={onChange}
                  title="From account"
                  placeholder="Enter name or number..."
                  list={fromAccounts}
                  onInfinateScroll={onScrollFromAccounts}
                  hasNextPage={fromAccountsPagination?.hasNextPage}
                  nextPageIsLoading={fromAccountsNextPageIsLoading}
                  onSearch={(search) =>
                    onSearchAutocomplete('fromAccount', search)
                  }
                  isLoading={fromAccountsListIsLoading}
                  error={errors?.fromAccountId?.message}
                  withInfiniteScroll
                  withCopySuffix
                  withSuffix
                />
              )}
            />
          </div>
          <div className={cn.formGroup}>
            <Controller
              control={control}
              name="toAccountId"
              defaultValue=""
              render={({ field: { onChange, value, name } }) => (
                <Autocomplete
                  name={name}
                  value={value as string}
                  onChange={onChange}
                  title="To account"
                  placeholder="Enter name or number..."
                  list={toAccounts}
                  onInfinateScroll={onScrollToAccounts}
                  hasNextPage={toAccountsPagination?.hasNextPage}
                  nextPageIsLoading={toAccountsNextPageIsLoading}
                  onSearch={(search) =>
                    onSearchAutocomplete('toAccount', search)
                  }
                  isLoading={toAccountsListIsLoading}
                  error={errors?.toAccountId?.message}
                  withInfiniteScroll
                  withCopySuffix
                  withSuffix
                />
              )}
            />
          </div>
          <div className={cn.formGroup}>
            <Controller
              control={control}
              name="rateSourceId"
              defaultValue=""
              render={({ field: { onChange, value, name } }) => (
                <Select
                  name={name}
                  value={value as string}
                  onChange={onChange}
                  title="Rate Source"
                  placeholder="Select rate sourcee"
                  error={errors?.rateSourceId?.message}
                  list={rateSource}
                />
              )}
            />
          </div>
        </>
      )}
      <div className={cn.separator} />
      {!general && (
        <div className={cn.maxAutoExecutionWrapper}>
          <div className={cn.maxAutoExecution}>
            <div className={cn.maxAutoExecutionTitle}>
              Max amount for auto-fx
            </div>
            <Controller
              control={control}
              defaultValue="SELL"
              name="exchangeType"
              render={({ field: { onChange, value } }) => (
                <div className={cn.exchangeTypeRadioWrapper}>
                  <Checkbox
                    name="sell"
                    checked={value === 'SELL'}
                    value="SELL"
                    onChange={(e) => {
                      onChange(e.target.value);

                      if (setValue && currencyFrom) {
                        setValue('maxAmountCurrencyId', currencyFrom);
                      }
                    }}
                    title="Sell"
                    type="radio"
                    labelClassName={cn.sellRadio}
                    disabled={!isEditable || !currencyFrom || !currencyTo}
                  />
                  <Checkbox
                    name="buy"
                    checked={value === 'BUY'}
                    value="BUY"
                    onChange={(e) => {
                      onChange(e.target.value);

                      if (setValue && currencyTo) {
                        setValue('maxAmountCurrencyId', currencyTo);
                      }
                    }}
                    title="Buy"
                    type="radio"
                    disabled={!isEditable || !currencyFrom || !currencyTo}
                  />
                </div>
              )}
            />
            <div className={cn.formGroup}>
              <Controller
                control={control}
                defaultValue=""
                name="minAmountPerOperation"
                render={({ field: { onChange, value, name } }) => (
                  <InputText
                    placeholder="Enter min amount per operation..."
                    name={name}
                    value={value}
                    title="Min amount per operation"
                    onChange={(e) => {
                      if (Number(e.target.value) < 0) {
                        onChange('0');
                      } else {
                        onChange(e.target.value);
                      }
                    }}
                    error={errors?.minAmountPerOperation?.message}
                    className={cn.inputWithSuffix}
                    suffix={
                      exchangeType === 'SELL'
                        ? currencies?.find((item) => item.id === currencyFrom)
                            ?.name
                        : currencies?.find((item) => item.id === currencyTo)
                            ?.name
                    }
                    currency={
                      exchangeType === 'SELL'
                        ? currencies?.find((item) => item.id === currencyFrom)
                        : currencies?.find((item) => item.id === currencyTo)
                    }
                    disabled={!isEditable || !currencyFrom || !currencyTo}
                    amountInput
                  />
                )}
              />
            </div>
            <div className={cn.formGroup}>
              <Controller
                control={control}
                defaultValue=""
                name="maxAmountPerOperation"
                render={({ field: { onChange, value, name } }) => (
                  <InputText
                    placeholder="Enter max amount per operation..."
                    name={name}
                    value={value}
                    title="Max amount per operation"
                    onChange={(e) => {
                      if (Number(e.target.value) < 0) {
                        onChange('0');
                      } else {
                        onChange(e.target.value);
                      }
                    }}
                    error={errors?.maxAmountPerOperation?.message}
                    className={cn.inputWithSuffix}
                    suffix={
                      exchangeType === 'SELL'
                        ? currencies?.find((item) => item.id === currencyFrom)
                            ?.name
                        : currencies?.find((item) => item.id === currencyTo)
                            ?.name
                    }
                    currency={
                      exchangeType === 'SELL'
                        ? currencies?.find((item) => item.id === currencyFrom)
                        : currencies?.find((item) => item.id === currencyTo)
                    }
                    disabled={!isEditable || !currencyFrom || !currencyTo}
                    amountInput
                  />
                )}
              />
            </div>
            <div className={cn.formGroupFlex}>
              <div className={cn.maxAmountPeriod}>
                <Controller
                  control={control}
                  name="maxAmountForPeriod"
                  defaultValue=""
                  render={({ field: { onChange, value, name } }) => (
                    <InputText
                      placeholder=""
                      name={name}
                      value={value}
                      title="Max amount for period"
                      onChange={(e) => {
                        if (Number(e.target.value) < 0) {
                          onChange('0');
                        } else {
                          onChange(e.target.value);
                        }
                      }}
                      error={errors?.maxAmountForPeriod?.message}
                      className={cn.inputWithSuffix}
                      suffix={
                        exchangeType === 'SELL'
                          ? currencies?.find((item) => item.id === currencyFrom)
                              ?.name
                          : currencies?.find((item) => item.id === currencyTo)
                              ?.name
                      }
                      currency={
                        exchangeType === 'SELL'
                          ? currencies?.find((item) => item.id === currencyFrom)
                          : currencies?.find((item) => item.id === currencyTo)
                      }
                      disabled={!isEditable || !currencyFrom || !currencyTo}
                      amountInput
                    />
                  )}
                />
              </div>
              <div className={cn.periodWrapper}>
                <Controller
                  control={control}
                  name="maxAmountPeriodInHours"
                  render={({ field: { onChange, value, name } }) => (
                    <InputNumber
                      placeholder=""
                      name={name}
                      value={value}
                      title="Days"
                      onChange={(e) => {
                        if (Number(e.target.value) < 0) {
                          onChange('0');
                        } else {
                          onChange(e.target.value);
                        }
                      }}
                      error={errors?.maxAmountPeriodInHours?.message}
                      className={cn.inputWithSuffix}
                      suffix="Days"
                      disabled={!isEditable || !currencyFrom || !currencyTo}
                    />
                  )}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      {((currencyFrom && currencyTo) || general) && (
        <div className={cn.scheduleWrapper}>
          <div className={cn.scheduleTitle}>Schedule for auto exchange</div>
          {schedule?.map((item) => (
            <Schedule
              key={item.weekday}
              schedule={item}
              setSchedule={setSchedule}
              schedules={schedule}
              isEditable={isEditable}
              withError={Boolean(
                weekdaysWithErrors?.find((weekday) => item.weekday === weekday),
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AutoSettingsForm;
